import React, { useContext } from "react";
import AppContext from "../../context/AppContext";

const highlightText = (text) => {
  if (typeof text !== "string") {
    text = "";
  }
  const stringRegex = /(".*?"|'.*?')/g;
  const keywordRegex =
    /\b(const|let|var|function|return|if|else|for|while|true|false|null|in|and)\b/g;
  const numberRegex = /\b(\d+)\b/g;
  const operatorRegex = /(\+|\-|\*|\/|\=|\!|\&|\||\?|\:|\%)/g;
  const tokens = text.split(/(\s+)/);
  const wrapSpan = (token, regex, className) => {
    return token.replace(regex, `<span class="${className}">$1</span>`);
  };
  const highlightedTokens = tokens.map((token) => {
    if (stringRegex.test(token)) {
      return wrapSpan(token, stringRegex, "text-green-500");
    } else if (keywordRegex.test(token)) {
      return wrapSpan(token, keywordRegex, "text-blue-500");
    } else if (numberRegex.test(token)) {
      return wrapSpan(token, numberRegex, "text-purple-500");
    } else if (operatorRegex.test(token)) {
      return wrapSpan(token, operatorRegex, "text-red-500");
    } else {
      return token;
    }
  });
  return highlightedTokens.join("");
};

// `showLineNumbers` opts into the prototype's code-editor look (line-number
// gutter, border moved to the wrapping row) for callers that want it --
// default layout/markup is unchanged for existing callers that don't pass it.
const HighlightedTextarea = ({ value, onChange, rows = 5, placeholder = "", showLineNumbers = false }) => {
  const { langMode } = useContext(AppContext)
  const lines = showLineNumbers ? (value || "").split("\n") : [];

  const textareaBox = (
    <div className="relative text-md flex-1 min-w-0">
      <div
        className="absolute inset-0 m-0 overflow-auto p-3 pointer-events-none whitespace-pre-wrap break-words text-[16px] leading-6"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: highlightText(value) }}
      />
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        className={`relative w-full text-md h-full bg-transparent p-3 outline-none text-transparent placeholder:text-gray-400 caret-black leading-6 transition-colors duration-200 ${langMode === 'ar' ? 'text-md' : 'text-md'} ${
          showLineNumbers
            ? "border-0 focus:ring-0"
            : "border border-gray-200 rounded-xl shadow-sm focus:border-gray-300 focus:ring-2 focus:ring-gray-100"
        }`}
      />
    </div>
  );

  if (!showLineNumbers) return textareaBox;

  return (
    <div className="flex rounded-xl border border-gray-200 shadow-sm overflow-hidden focus-within:border-gray-300 focus-within:ring-2 focus-within:ring-gray-100">
      <div className="select-none bg-gray-50 border-r border-gray-100 px-3 py-3 text-right">
        {lines.map((_, i) => (
          <div key={i} className="font-mono text-xs leading-6 text-gray-300">
            {i + 1}
          </div>
        ))}
      </div>
      {textareaBox}
    </div>
  );
};

export default HighlightedTextarea;
